import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/auth";

/* ============================================================
   /crown — THE CROWN ROOM · Two Crowns celebration + share.

   Displays both monthly kings side-by-side:
     • ARENA CROWN  — top rapper by monthly points (best battler)
     • CHARTS CROWN — top-scoring track this month
   Each crown has its own share buttons. Public page — no sign-in
   required to view or share. Resets on the 1st of every month.
   ============================================================ */

const STYLE_LABELS = {
  cinematic_pop: "CINEMATIC POP", inspirational_ballad: "BALLAD",
  singer_songwriter: "SINGER-SONGWRITER", indie_anthem: "INDIE ANTHEM",
  worship_gospel: "GOSPEL",
  aggressive: "AGGRESSIVE", boom_bap: "BOOM BAP", trap: "TRAP",
  smooth: "SMOOTH", conscious: "CONSCIOUS", hip_hop: "HIP HOP",
  drill: "DRILL", country: "COUNTRY", pop: "POP", rnb: "R&B",
  edm: "EDM", latin: "LATIN", rock: "ROCK",
};

function monthName(monthKey) {
  if (!monthKey) return "";
  const [y, m] = monthKey.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString(undefined, { month: "long", year: "numeric" }).toUpperCase();
}

export default function MonthlyCrown() {
  const [arena, setArena] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/arena/champion`).then(r => r.data).catch(() => null),
      axios.get(`${API}/charts/champion`).then(r => r.data).catch(() => null),
    ]).then(([a, c]) => {
      setArena(a || null);
      setCharts(c || null);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-center font-mono text-yt-muted animate-pulse-red">LOADING THE CROWN ROOM...</div>;
  }

  const month = monthName(arena?.month || charts?.month);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      {/* HEADER */}
      <div className="text-center mb-10">
        <div className="font-mono text-xs tracking-[0.5em] text-yt-yellow mb-3">
          THE CROWN ROOM · {month || "THIS MONTH"}
        </div>
        <h1 className="font-display text-6xl md:text-8xl tracking-tight leading-[0.9]">
          <span className="text-yt-red">TWO</span>{" "}
          <span className="text-yt-yellow">CROWNS</span>
        </h1>
        <p className="font-mono text-[10px] md:text-xs tracking-widest text-yt-muted mt-4 max-w-2xl mx-auto">
          BATTLE KING · SONG KING · CROWNED BY THE COMMUNITY · CERTIFIED BY THE JUDGE
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ArenaCrownCard arena={arena} month={month} />
        <ChartsCrownCard champ={charts} month={month} />
      </div>

      <div className="mt-10 text-center">
        <p className="font-mono text-xs tracking-widest text-yt-muted max-w-2xl mx-auto">
          The crown resets on the 1st of every month. Screenshot a card to post on IG, TikTok, or Twitter. Tag @youngtongues.
        </p>
        <div className="mt-6 flex gap-3 flex-wrap justify-center">
          <Link to="/leaderboard" data-testid="crown-leaderboard-link"
            className="border-2 border-yt-red text-yt-red font-display text-lg tracking-widest px-5 py-3 hover:bg-yt-red hover:text-white">
            ARENA LEADERBOARD
          </Link>
          <Link to="/charts" data-testid="crown-charts-link"
            className="border-2 border-yt-yellow text-yt-yellow font-display text-lg tracking-widest px-5 py-3 hover:bg-yt-yellow hover:text-black">
            THE CHARTS
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------
   ARENA CROWN — top rapper of the month
   ------------------------------------------------------------ */
function ArenaCrownCard({ arena, month }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/crown` : "";
  const shareText = arena
    ? `👑 The ARENA CROWN belongs to @${arena.rapper_name} — ${arena.wins}W-${arena.losses}L · ${arena.monthly_points} pts this month on Young Tongues.`
    : "";

  const share = async () => {
    if (!arena) return;
    if (navigator.share) {
      try { await navigator.share({ title: "Young Tongues · Arena Crown", text: shareText, url: shareUrl }); return; } catch (e) {}
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  if (!arena) {
    return (
      <div data-testid="arena-crown-empty" className="border-4 border-dashed border-yt-red/50 bg-yt-surface p-8 md:p-10 text-center">
        <div className="text-6xl md:text-7xl mb-4 opacity-40">🥊</div>
        <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-3">ARENA CROWN · UNCLAIMED</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-4">UP FOR GRABS</h2>
        <p className="font-mono text-sm text-yt-muted mb-6">
          Nobody's won a battle yet this month. Be the first to claim the ring.
        </p>
        <Link to="/battles/new" data-testid="arena-crown-cta"
          className="inline-block bg-yt-red text-white font-display text-lg tracking-widest px-6 py-3 shadow-brutal hover:bg-white hover:text-black">
          ▶ ENTER THE ARENA
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="arena-crown-card"
      className="relative border-4 border-yt-red bg-gradient-to-br from-yt-red via-black to-yt-surface shadow-brutal overflow-hidden p-6 md:p-8"
    >
      <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-white" />
      <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-white" />
      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-white" />
      <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-white" />

      <div className="text-center">
        <div className="text-6xl md:text-7xl mb-3">🥊</div>
        <div className="font-mono text-xs tracking-[0.5em] text-white mb-2">ARENA CROWN · {month}</div>
        <div className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-1 text-white">
          BATTLE KING
        </div>
        <div className="font-mono text-[10px] tracking-[0.4em] text-white/70 mb-6">CROWNED BY THE RING</div>

        <h2 data-testid="arena-crown-name" className="font-display text-3xl md:text-5xl tracking-tight mb-2 text-white break-words">
          @{arena.rapper_name}
        </h2>
        {arena.style && <div className="font-mono text-xs tracking-widest text-yt-yellow mb-4">{arena.style.toUpperCase()}</div>}

        <div className="inline-block bg-black border-2 border-white px-5 py-3 font-mono text-[11px] tracking-widest text-white mb-4">
          <span data-testid="arena-crown-record">{arena.wins}W · {arena.losses}L</span>
          <span className="text-yt-yellow ml-3">· {arena.monthly_points} PTS</span>
          {arena.streak > 1 && <span className="text-yt-yellow ml-3">· 🔥 {arena.streak}</span>}
        </div>

        <div className="mt-3 flex gap-2 flex-wrap justify-center">
          <button
            data-testid="arena-crown-share"
            onClick={share}
            className="bg-white text-black font-display text-sm tracking-widest px-5 py-3 shadow-brutal hover:bg-yt-yellow"
          >
            {copied ? "✓ COPIED" : "SHARE THE CROWN ▶"}
          </button>
          <Link
            to={`/profile/${encodeURIComponent(arena.rapper_name)}`}
            data-testid="arena-crown-profile"
            className="border-2 border-white text-white font-display text-sm tracking-widest px-5 py-3 hover:bg-white hover:text-black"
          >
            VIEW PROFILE
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------
   CHARTS CROWN — top-scoring track of the month
   ------------------------------------------------------------ */
function ChartsCrownCard({ champ, month }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/crown` : "";
  const shareText = champ
    ? `👑 The CHARTS CROWN belongs to ${champ.anonymous ? "an anonymous artist" : "@" + champ.publish_as} — "${champ.title}" on Young Tongues. Come vote.`
    : "";

  const share = async () => {
    if (!champ) return;
    if (navigator.share) {
      try { await navigator.share({ title: "Young Tongues · Charts Crown", text: shareText, url: shareUrl }); return; } catch (e) {}
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  if (!champ) {
    return (
      <div data-testid="charts-crown-empty" className="border-4 border-dashed border-yt-yellow/50 bg-yt-surface p-8 md:p-10 text-center">
        <div className="text-6xl md:text-7xl mb-4 opacity-40">👑</div>
        <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">CHARTS CROWN · UNCLAIMED</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-4">UP FOR GRABS</h2>
        <p className="font-mono text-sm text-yt-muted mb-6">
          No tracks scored this month. Be the first to drop and take the wall.
        </p>
        <Link to="/charts/submit" data-testid="charts-crown-cta"
          className="inline-block bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 shadow-brutal hover:bg-white">
          ▶ DROP A TRACK
        </Link>
      </div>
    );
  }

  const styleLabel = STYLE_LABELS[champ.style] || (champ.style || "").toUpperCase();

  return (
    <div
      data-testid="charts-crown-card"
      className="relative border-4 border-yt-yellow bg-gradient-to-br from-yt-yellow/40 via-black to-yt-surface shadow-brutal overflow-hidden p-6 md:p-8"
    >
      <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-black" />
      <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-black" />
      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-black" />
      <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-black" />

      <div className="text-center">
        <div className="text-6xl md:text-7xl mb-3">👑</div>
        <div className="font-mono text-xs tracking-[0.5em] text-yt-yellow mb-2">CHARTS CROWN · {month}</div>
        <div className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-1 text-white">
          SONG KING
        </div>
        <div className="font-mono text-[10px] tracking-[0.4em] text-white/70 mb-6">CROWNED BY THE COMMUNITY</div>

        <div className="font-mono text-xs tracking-widest text-yt-yellow mb-2">
          {styleLabel}{champ.explicit ? " · 🅴" : ""}
        </div>
        <h2 data-testid="charts-crown-title" className="font-display text-3xl md:text-5xl tracking-tight mb-2 text-white break-words">
          {champ.title}
        </h2>
        <div data-testid="charts-crown-artist" className="font-mono text-sm tracking-widest text-yt-yellow mb-4">
          {champ.anonymous ? "◇ ANONYMOUS ARTIST" : `@${champ.publish_as}`}
        </div>

        {champ.audio_url && (
          <audio
            data-testid="charts-crown-audio"
            src={champ.audio_url}
            controls
            className="w-full max-w-md mx-auto mb-4"
          />
        )}

        <div className="inline-block bg-black text-yt-yellow font-display text-xl md:text-2xl tracking-widest px-5 py-3 border-2 border-yt-yellow mb-4">
          SCORE · {champ.score}
        </div>

        <div className="mt-3 flex gap-2 flex-wrap justify-center">
          <button
            data-testid="charts-crown-share"
            onClick={share}
            className="bg-yt-yellow text-black font-display text-sm tracking-widest px-5 py-3 shadow-brutal hover:bg-white"
          >
            {copied ? "✓ COPIED" : "SHARE THE CROWN ▶"}
          </button>
          <Link
            to={`/tracks/${champ.id}`}
            data-testid="charts-crown-view"
            className="border-2 border-yt-yellow text-yt-yellow font-display text-sm tracking-widest px-5 py-3 hover:bg-yt-yellow hover:text-black"
          >
            VIEW TRACK
          </Link>
        </div>
      </div>
    </div>
  );
}
